import { useEffect, useRef, useState } from "react";

const FRAME_HEADER_BYTES = 3;
const TIMESTAMP_BYTES = 8;
const STALE_AFTER_MS = 2000;
const BACKOFF_SCHEDULE_MS = [1000, 2000, 5000, 10000];
const STABLE_CONNECTION_MS = 30000;
const PROTOCOL_VERSION = 1;
const FRAME_TYPE_FRAME = 0;
const MAX_CONSECUTIVE_DECODE_FAILURES = 5;

export interface CameraStreamState {
  connected: boolean;
  stale: boolean;
  lastFrameAt: number | null;
  error: string | null;
}

interface UseCameraStreamOptions {
  wsUrl: string;
  enabled: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
}

interface ParsedFrame {
  type: number;
  cameraId: string;
  captureTsMs: number;
  jpeg: ArrayBuffer;
}

function parseFrame(data: ArrayBuffer): ParsedFrame | null {
  if (data.byteLength < FRAME_HEADER_BYTES + TIMESTAMP_BYTES) {
    return null;
  }
  const view = new DataView(data);
  const version = view.getUint8(0);
  const type = view.getUint8(1);
  if (version !== PROTOCOL_VERSION || type !== FRAME_TYPE_FRAME) {
    return null;
  }
  const idLen = view.getUint8(2);
  const idEnd = FRAME_HEADER_BYTES + idLen;
  const tsEnd = idEnd + TIMESTAMP_BYTES;
  if (data.byteLength < tsEnd) {
    return null;
  }
  const cameraId = new TextDecoder().decode(new Uint8Array(data, FRAME_HEADER_BYTES, idLen));
  const captureTsMs = Number(view.getBigUint64(idEnd));
  return { type, cameraId, captureTsMs, jpeg: data.slice(tsEnd) };
}

/**
 * Opens a WebSocket to the camera relay and paints incoming JPEG frames to a
 * canvas. Latest-frame-only: a frame arriving while a decode is in flight
 * replaces the pending one rather than queueing, so a slow tab cannot fall
 * behind. Frames are painted imperatively (no React re-render per frame).
 */
export function useCameraStream({ wsUrl, enabled, canvasRef }: UseCameraStreamOptions): CameraStreamState {
  const [connected, setConnected] = useState(false);
  const [stale, setStale] = useState(false);
  const [lastFrameAt, setLastFrameAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lastFrameAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !wsUrl) {
      return;
    }

    let disposed = false;
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffIndex = 0;
    let connectedAt = 0;

    let decoding = false;
    let pendingJpeg: ArrayBuffer | null = null;
    let consecutiveDecodeFailures = 0;

    const paint = (jpeg: ArrayBuffer) => {
      if (decoding) {
        pendingJpeg = jpeg;
        return;
      }
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      decoding = true;
      void createImageBitmap(new Blob([jpeg]))
        .then((bitmap) => {
          if (disposed) {
            bitmap.close();
            return;
          }
          consecutiveDecodeFailures = 0;
          setError(null);
          if (canvas.width !== bitmap.width || canvas.height !== bitmap.height) {
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
          }
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(bitmap, 0, 0);
          }
          bitmap.close();
        })
        .catch(() => {
          consecutiveDecodeFailures += 1;
          if (consecutiveDecodeFailures >= MAX_CONSECUTIVE_DECODE_FAILURES) {
            setError(`frame decode failed (${consecutiveDecodeFailures} consecutive)`);
          }
          pendingJpeg = null;
        })
        .finally(() => {
          decoding = false;
          if (pendingJpeg && !disposed) {
            const next = pendingJpeg;
            pendingJpeg = null;
            paint(next);
          }
        });
    };

    const connect = () => {
      if (disposed) {
        return;
      }
      const socket = new WebSocket(wsUrl);
      socket.binaryType = "arraybuffer";
      ws = socket;

      socket.addEventListener("open", () => {
        if (disposed) {
          return;
        }
        setConnected(true);
        setError(null);
        connectedAt = Date.now();
        backoffIndex = 0;
      });

      socket.addEventListener("message", (event: MessageEvent) => {
        if (!(event.data instanceof ArrayBuffer)) {
          return;
        }
        const frame = parseFrame(event.data);
        if (!frame || frame.jpeg.byteLength === 0) {
          return;
        }
        const now = Date.now();
        lastFrameAtRef.current = now;
        setLastFrameAt(now);
        setStale(false);
        paint(frame.jpeg);
      });

      const scheduleReconnect = () => {
        if (disposed) {
          return;
        }
        setConnected(false);
        if (connectedAt && Date.now() - connectedAt >= STABLE_CONNECTION_MS) {
          backoffIndex = 0;
        }
        const base = BACKOFF_SCHEDULE_MS[Math.min(backoffIndex, BACKOFF_SCHEDULE_MS.length - 1)];
        const delay = base + Math.random() * base * 0.25;
        backoffIndex += 1;
        reconnectTimer = setTimeout(connect, delay);
      };

      socket.addEventListener("close", scheduleReconnect);
      socket.addEventListener("error", () => {
        setError("connection error");
        socket.close();
      });
    };

    connect();

    const staleTimer = setInterval(() => {
      const last = lastFrameAtRef.current;
      setStale(last === null || Date.now() - last > STALE_AFTER_MS);
    }, 1000);

    return () => {
      disposed = true;
      clearInterval(staleTimer);
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (ws) {
        ws.close();
      }
      setConnected(false);
    };
  }, [enabled, wsUrl, canvasRef]);

  return { connected, stale, lastFrameAt, error };
}
